import React from 'react';
import PropTypes from 'prop-types';

import get from 'lodash/get';
import { Link } from 'react-router-dom';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';

import CreditTransferPolicies, {
  getCreditTransferPolicies,
} from './CreditTransferPolicies';
import DualAdmissionAgreements, {
  getDualAdmissionAgreements,
} from './DualAdmissionsAgreements';
import { getAllArticulationAgreements } from '../utils';
import ArticulationAgreements from './ArticulationAgreements';

const messages = defineMessages({
  transferCredits: {
    id: 'hubs.transfer.transferring-credits.title',
    defaultMessage: 'What to Consider When Transferring Credits',
  },
  transferCreditsText: {
    id: 'hubs.transfer.transferring-credits.text',
    defaultMessage: `Every student's college transfer path is unique. <a>Contact the college directly</a> for information about how your specific credits will transfer.`,
  },
  creditTransferPolicies: {
    id: 'hubs.transfer.credit-transfer-policies.title',
    defaultMessage: 'Credit Transfer Policies',
  },
  creditTransferPoliciesText: {
    id: 'hubs.transfer.credit-transfer-policies.text',
    defaultMessage:
      'Each college follows its own policies on which credits it will accept when a student transfers. Often an individual review of your specific coursework can be provided.',
  },
  articulationAgreements: {
    id: 'hubs.transfer.articulation-agreements.title',
    defaultMessage: 'Colleges with Articulation Agreements',
  },
  articulationAgreementsText: {
    id: 'hubs.transfer.articulation-agreements.text',
    defaultMessage:
      'Articulation agreements are written contracts between two or more colleges that spell out exactly what is required to transfer from one to the other. These agreements often include detailed information on what credits will transfer, course requirements, if there’s guaranteed admission and how that works, and if there are scholarships available.',
  },
  dualAdmissionAgreements: {
    id: 'hubs.transfer.dual-admission-agreements.title',
    defaultMessage: 'Colleges with Dual Admissions Agreements',
  },
  dualAdmissionAgreementsText: {
    id: 'hubs.transfer.dual-admission-agreements.text',
    defaultMessage:
      'Some colleges offer dual admissions programs, in which the student applies to both the community college and the four-year college at the same time. If admitted, the student is guaranteed a spot as a transfer student at the four-year college, as long as the student obtains an associate’s degree from the community college.',
  },
});

function SubSectionWrapper({ text, title, content, cypressKey }) {
  return (
    <div className="subsection" data-cy={cypressKey}>
      <p className="margin-0 subsection-title">{title}</p>
      <p className="section-text">{text}</p>
      <div className="subsection-content">{content}</div>
    </div>
  );
}

function TransferringCredits({
  intl,
  getUrls,
  transferAdmissions,
  scrollContactInfoIntoView,
}) {
  const creditTransferPolicies = getCreditTransferPolicies(
    get(transferAdmissions, 'admissionPolicies.value', []),
  );
  const articulationAgreements = getAllArticulationAgreements(
    get(transferAdmissions, 'articulationAgreementsSystem.value', []),
    get(transferAdmissions, 'articulationAgreements.value', []),
  );
  const dualAdmissionAgreements = getDualAdmissionAgreements(
    get(transferAdmissions, 'dualAdmissions.value', []),
  );

  return (
    <div
      data-cy="transfer-credits"
      name="transferring-credits"
      className="transfer-common-container common-core-width"
    >
      <p className="section-title margin-bottom-half">
        {intl.formatMessage(messages.transferCredits)}
      </p>
      <p className="section-text">
        <FormattedMessage
          id={messages.transferCreditsText.id}
          defaultMessage={messages.transferCreditsText.defaultMessage}
          values={{
            a: (...chunks) => (
              <Link to="#" onClick={scrollContactInfoIntoView}>
                {chunks}
              </Link>
            ),
          }}
        />
      </p>
      {creditTransferPolicies.length ? (
        <SubSectionWrapper
          cypressKey="credit-transfer-policies"
          title={intl.formatMessage(messages.creditTransferPolicies)}
          text={intl.formatMessage(messages.creditTransferPoliciesText)}
          content={
            <CreditTransferPolicies
              intl={intl}
              creditTransferPolicies={creditTransferPolicies}
            />
          }
        />
      ) : null}
      {articulationAgreements.length ? (
        <SubSectionWrapper
          cypressKey="articulation-agreements"
          title={intl.formatMessage(messages.articulationAgreements)}
          text={intl.formatMessage(messages.articulationAgreementsText)}
          content={
            <ArticulationAgreements
              getUrls={getUrls}
              articulationAgreements={articulationAgreements}
            />
          }
        />
      ) : null}
      {dualAdmissionAgreements.length ? (
        <SubSectionWrapper
          cypressKey="dual-admission-agreements"
          title={intl.formatMessage(messages.dualAdmissionAgreements)}
          text={intl.formatMessage(messages.dualAdmissionAgreementsText)}
          content={
            <DualAdmissionAgreements
              getUrls={getUrls}
              dualAdmissionAgreements={dualAdmissionAgreements}
            />
          }
        />
      ) : null}
    </div>
  );
}

TransferringCredits.propTypes = {
  intl: PropTypes.object,
  getUrls: PropTypes.func,
  transferAdmissions: PropTypes.object,
};

export default injectIntl(TransferringCredits);
